import { useEffect, useState } from 'react';
import { Theme, ThemeProviderContext } from '@/hooks/use-theme';

type ThemeProviderProps = {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
};

function applyTheme(theme: Theme) {
  const root = window.document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(theme);

  const color = getComputedStyle(root).getPropertyValue('--background').trim();
  const metaThemeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  if (metaThemeColor && color) metaThemeColor.content = color;
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
  storageKey = 'vite-ui-theme',
}: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem(storageKey) as Theme | null;
    // migrate away from legacy 'system' value
    if (stored === 'dark' || stored === 'light') return stored;
    return defaultTheme;
  });

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  const setTheme = (newTheme: Theme, origin?: { x: number; y: number }) => {
    localStorage.setItem(storageKey, newTheme);

    const supportsViewTransition =
      'startViewTransition' in document &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!supportsViewTransition || !origin) {
      setThemeState(newTheme);
      return;
    }

    const { x, y } = origin;
    // radius large enough to cover the farthest corner of the viewport
    const r = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));

    const root = document.documentElement;
    root.style.setProperty('--vt-x', `${x}px`);
    root.style.setProperty('--vt-y', `${y}px`);
    root.style.setProperty('--vt-r', `${r}px`);

    (document as Document & { startViewTransition: (cb: () => void) => void }).startViewTransition(
      () => {
        setThemeState(newTheme);
        applyTheme(newTheme);
      },
    );
  };

  return (
    <ThemeProviderContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeProviderContext.Provider>
  );
}
